import React, { useEffect, useCallback } from 'react';
import { RoadmapProject } from '../types';
import {
  getStatusClass,
  getStatusLabel,
  getProjectQuarterInfo,
  getInitials,
  formatCompactCurrency,
} from '../utils/roadmapUtils';

interface ProjectDetailPanelProps {
  project: RoadmapProject | null;
  isOpen: boolean;
  onClose: () => void;
}

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="detail-section">
    <div className="detail-section-label">{label}</div>
    {children}
  </div>
);

const TextValue: React.FC<{ value: string | undefined | null; fallback?: string }> = ({
  value,
  fallback = 'Not specified',
}) => (
  <p className={`detail-section-value${!value ? ' muted' : ''}`}>{value || fallback}</p>
);

export const ProjectDetailPanel: React.FC<ProjectDetailPanelProps> = ({ project, isOpen, onClose }) => {
  // Esc key to dismiss
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!project && !isOpen) return null;

  const quarterInfo = project ? getProjectQuarterInfo(project) : null;
  const statusClass = project ? getStatusClass(project.status) : '';
  const statusLabel = project ? getStatusLabel(project.status) : '';
  const assigneeInitials = project?.assignee ? getInitials(project.assignee.name) : null;
  const managerInitials = project?.l7_manager ? getInitials(project.l7_manager) : null;

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      {/* Overlay — click to close */}
      {isOpen && (
        <div
          className="panel-overlay"
          onClick={onClose}
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Slide-in panel */}
      <div
        className={`detail-panel${isOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={project?.name ?? 'Project details'}
      >
        {project && (
          <>
            {/* Header */}
            <div className="detail-panel-header">
              <div className="detail-panel-top-row">
                <h2 className="detail-panel-title">{project.name}</h2>
                <button
                  className="panel-close-btn"
                  onClick={onClose}
                  aria-label="Close panel"
                  type="button"
                >
                  ×
                </button>
              </div>

              <div className="detail-panel-badges">
                <span className={`status-badge ${statusClass}`}>
                  <span className="status-badge-dot" aria-hidden="true" />
                  {statusLabel}
                </span>
                {quarterInfo && (
                  <span className="quarter-pill-small">{quarterInfo.key}</span>
                )}
                {project.launch_date && (
                  <span className="launch-date-labeled">
                    <span className="launch-date-label">Launch Date:</span>
                    {formatDate(project.launch_date)}
                  </span>
                )}
              </div>

              {project.permalink_url && (
                <a
                  href={project.permalink_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="asana-link-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="11.5" cy="6.5" r="4.5" />
                    <circle cx="19.5" cy="17.5" r="4.5" />
                    <circle cx="3.5" cy="17.5" r="4.5" />
                  </svg>
                  Open in Asana
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              )}
            </div>

            {/* Scrollable body */}
            <div className="detail-panel-body">
              {/* Problem Statement */}
              <Section label="Problem Statement">
                <TextValue value={project.problem_statement} />
              </Section>

              {/* Desired Outcome */}
              <Section label="Desired Outcome">
                <TextValue value={project.desired_outcome} />
              </Section>

              {/* Business Impact */}
              <Section label="Business Impact">
                <TextValue value={project.business_impact} />
              </Section>

              {/* Key Deliverables */}
              <Section label="Key Deliverables">
                <TextValue value={project.key_deliverables} />
              </Section>

              <div className="detail-divider" />

              {/* Metrics */}
              <Section label="Metrics">
                <div className="detail-metrics-grid">
                  <div className="detail-metric-card">
                    <div className="detail-metric-value">
                      {formatCompactCurrency(project.annualized_entitlement)}
                    </div>
                    <div className="detail-metric-label">Annualised Entitlement</div>
                  </div>
                  <div className="detail-metric-card">
                    <div className="detail-metric-value">
                      {project.hc_sde > 0 ? project.hc_sde.toFixed(1) : '—'}
                    </div>
                    <div className="detail-metric-label">SDE HC</div>
                  </div>
                </div>
              </Section>

              <div className="detail-divider" />

              {/* Assignee + L7 Manager — side by side, centered */}
              {(project.assignee || project.l7_manager) && (
                <div className="detail-people-row">
                  {project.assignee && (
                    <div className="detail-person-card">
                      <div className="detail-assignee-avatar">{assigneeInitials}</div>
                      <span className="detail-assignee-name">{project.assignee.name}</span>
                      <span className="detail-assignee-role">Product Owner</span>
                    </div>
                  )}
                  {project.l7_manager && (
                    <div className="detail-person-card">
                      <div className="detail-assignee-avatar" style={{ background: '#af52de' }}>
                        {managerInitials}
                      </div>
                      <span className="detail-assignee-name">{project.l7_manager}</span>
                      <span className="detail-assignee-role">L7 Manager</span>
                    </div>
                  )}
                </div>
              )}

              {/* Program */}
              <Section label="Program">
                <p className="detail-section-value">{project.program || 'Unassigned'}</p>
              </Section>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProjectDetailPanel;